import type { Filter } from "connected-components/dashboard/context/types"
import type { Normalization } from "connected-components/dashboard/helpers"
import { computeModeFilteredSegment } from "helpers/segments"
import type {
  ClickPosition,
  DrilldownFrozenSnapshot,
  DrilldownIntent,
  DrilldownIntentSeed,
  DrilldownSurface,
} from "./types"

export interface DrilldownContext {
  modelId: string
  instrument?: Record<string, unknown> | null
  base?: Record<string, unknown> | null
  comparisonInstrument?: Record<string, unknown> | null
  secondInstrument?: Record<string, unknown> | null
  startDate: string
  endDate: string
  isIntradayMode: boolean
  intradayAsOf?: string | null
  batchId?: string | null
  filter?: Filter
  normalization?: Normalization
  isEndVsStartDateComparison?: boolean
}

type DrilldownSeedOverrides = Partial<
  Omit<DrilldownIntentSeed, "frozenSnapshot" | "view" | "sourcePanelId">
> & {
  frozenSnapshot?: Partial<DrilldownFrozenSnapshot>
}

export interface BuildDrilldownIntentParams {
  panelId: string
  sourceSurface: DrilldownSurface
  title: string
  view: string
  context: DrilldownContext
  clickPosition?: ClickPosition
  seed?: DrilldownSeedOverrides
}

export function buildDrilldownIntent({
  panelId,
  sourceSurface,
  title,
  view,
  context,
  clickPosition,
  seed = {},
}: BuildDrilldownIntentParams): DrilldownIntent {
  const { frozenSnapshot: frozenSnapshotOverride, ...seedFields } = seed
  const { modelId, instrument, base, comparisonInstrument, secondInstrument, startDate, endDate, isIntradayMode, intradayAsOf, batchId, filter, normalization, isEndVsStartDateComparison } = context

  return {
    sourcePanelId: panelId,
    sourceSurface,
    title,
    targetModuleType: "asset-view",
    floating: true,
    transient: true,
    seed: {
      ...seedFields,
      view,
      sourcePanelId: panelId,
      frozenSnapshot: {
        modelId,
        positionSet: instrument ?? null,
        base: base ?? null,
        // Always set (value-or-null) rather than conditionally spread: the refocus merge is
        // {...existing, ...intent}, so an omitted field would let a stale value survive when the
        // source clears compare/second-instrument/intraday and re-drills. An explicit null clears it.
        comparisonInstrument: comparisonInstrument ?? null,
        secondInstrument: secondInstrument ?? null,
        startDate,
        endDate,
        isIntradayMode,
        intradayAsOf: intradayAsOf ?? null,
        batchId: batchId ?? null,
        isEndVsStartDateComparison: Boolean(isEndVsStartDateComparison),
        segment: computeModeFilteredSegment(filter, normalization, isIntradayMode),
        ...frozenSnapshotOverride,
      },
    },
    ...(clickPosition && { clickPosition }),
  }
}
